import React, { useId } from 'react';
import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import ExportCsvMenu from './ExportCsvMenu';
import './TableControls.css';

/**
 * Search, one filter select, the export menu and the "N of M" count above a
 * coordinator table. The select is the tab's one primary filter (a status, a
 * course, whether places are open); `statusOptions` null leaves it out.
 * `summary` replaces the count text, and `onExportFull`/`exporting` add the
 * server's full spreadsheet to the export menu. `searchTitle` puts a visible label
 * above the search box in place of the "Search" prefix; the box's accessible
 * name (`searchLabel`) starts with the same words. The controls line up on
 * their bottom edge, so a labelled search box and an unlabelled select share
 * one baseline. `departmentOptions` adds a department select beside the status
 * one, and the row then runs four across from the lg breakpoint.
 */
export default function TableControlsBar({
    controls,
    noun,
    searchLabel,
    searchPlaceholder,
    searchTitle = null,
    statusLabel,
    statusOptions = null,
    total = null,
    allLabel = 'All Statuses',
    departmentOptions = null,
    departmentLabel = 'Filter by department',
    exportFilename,
    summary = null,
    onExportFull = null,
    fullNote = '',
    exporting = false,
    clearLabel = 'Clear filters',
}) {
    const id = useId();
    // With the department select the controls wrap into two rows from md to
    // lg, so the columns keep their bottom margin until lg
    const gap = departmentOptions ? 'mb-2 mb-lg-0' : 'mb-2 mb-md-0';
    return (
        <Row className="align-items-end table-controls-bar">
            <Col md={6} lg={departmentOptions ? 3 : 6} className={gap}>
                {searchTitle ? (
                    <>
                        <Form.Label htmlFor={`${id}-search`} className="mb-1">
                            {searchTitle}
                        </Form.Label>
                        <Form.Control
                            id={`${id}-search`}
                            ref={controls.searchRef}
                            aria-label={searchLabel}
                            placeholder={searchPlaceholder}
                            value={controls.search}
                            onChange={(event) =>
                                controls.setSearch(event.target.value)
                            }
                        />
                    </>
                ) : (
                    <InputGroup>
                        <InputGroup.Text id={`${id}-search-label`}>
                            Search
                        </InputGroup.Text>
                        <Form.Control
                            ref={controls.searchRef}
                            aria-label={searchLabel}
                            placeholder={searchPlaceholder}
                            value={controls.search}
                            onChange={(event) =>
                                controls.setSearch(event.target.value)
                            }
                        />
                    </InputGroup>
                )}
            </Col>
            <Col md={departmentOptions ? 6 : 3} lg={3} className={gap}>
                {statusOptions && (
                    <Form.Select
                        aria-label={statusLabel}
                        value={controls.status}
                        onChange={(event) =>
                            controls.setStatus(event.target.value)
                        }
                    >
                        <option value="all">{allLabel}</option>
                        {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                )}
            </Col>
            {departmentOptions && (
                <Col md={6} lg={3} className={gap}>
                    <Form.Select
                        aria-label={departmentLabel}
                        value={controls.department}
                        onChange={(event) =>
                            controls.setDepartment(event.target.value)
                        }
                    >
                        <option value="all">All Departments</option>
                        {departmentOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            )}
            <Col md={departmentOptions ? 6 : 3} lg={3} className="text-md-end">
                <ExportCsvMenu
                    columns={controls.columns}
                    allRows={controls.allRows}
                    filteredRows={controls.rows}
                    filtersActive={controls.filtersActive}
                    filename={exportFilename}
                    className="mb-1"
                    onExportFull={onExportFull}
                    fullNote={fullNote}
                    exporting={exporting}
                />
                <small className="text-muted d-block" aria-live="polite">
                    {summary ??
                        `${controls.rows.length} of ${total ?? controls.total} ${noun}`}
                </small>
                {controls.filtersActive && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={controls.clearFilters}
                    >
                        {clearLabel}
                    </Button>
                )}
            </Col>
        </Row>
    );
}
